import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart3, ArrowLeft, Building2 } from 'lucide-react';
import { authApi } from '@/lib/api';
import { toast } from 'sonner';
import { setAuthData } from '@/lib/utils';

export default function SponsorLogin() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  
  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register form
  const [regCompany, setRegCompany] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) return;
    
    setIsLoading(true);
    try {
      const response = await authApi.sponsorLogin(loginEmail, loginPassword);
      setAuthData(response.data.token, { ...response.data.sponsor, user_type: 'sponsor' });
      toast.success('Welcome back!');
      navigate('/sponsor/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regCompany || !regEmail || !regPassword) return;
    
    setIsLoading(true);
    try {
      const response = await authApi.sponsorRegister(regCompany, regEmail, regPassword);
      setAuthData(response.data.token, { ...response.data.sponsor, user_type: 'sponsor' });
      toast.success('Account created successfully!');
      navigate('/sponsor/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="sponsor-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Back link */}
        <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
        
        {/* Card */}
        <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Sponsor Portal</h1>
              <p className="text-sm text-slate-500">Manage your campaigns</p>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full bg-slate-100 rounded-full p-1 mb-6">
              <TabsTrigger 
                value="login" 
                className="flex-1 rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="login-tab"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger 
                value="register" 
                className="flex-1 rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="register-tab"
              >
                Register
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Email</label>
                  <Input
                    type="email"
                    placeholder="sponsor@company.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="bg-slate-50 border-slate-200 rounded-xl h-12"
                    data-testid="login-email-input"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Password</label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="bg-slate-50 border-slate-200 rounded-xl h-12"
                    data-testid="login-password-input"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full h-12"
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
              
              {/* Demo credentials hint */}
              <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-600">
                  <strong>Demo credentials:</strong><br />
                  Email: demo@toyota.com<br />
                  Password: demo123
                </p>
              </div>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Company Name</label>
                  <Input
                    placeholder="Your Company Inc."
                    value={regCompany}
                    onChange={(e) => setRegCompany(e.target.value)}
                    className="bg-slate-50 border-slate-200 rounded-xl h-12"
                    data-testid="register-company-input"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Email</label>
                  <Input
                    type="email"
                    placeholder="sponsor@company.com"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    className="bg-slate-50 border-slate-200 rounded-xl h-12"
                    data-testid="register-email-input"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Password</label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    className="bg-slate-50 border-slate-200 rounded-xl h-12"
                    data-testid="register-password-input"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full h-12"
                  disabled={isLoading}
                  data-testid="register-submit-btn"
                >
                  {isLoading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </motion.div>
    </div>
  );
}
